import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { translate } from '@jsverse/transloco';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { switchMap, map, tap, catchError, take } from 'rxjs/operators';
import { AppNotificationService } from '../../shared/services/notification.service';
import { ICreateWearingPartRequest, ICreateWearingPartResponse, IGetWearingPartResponse, IUpdateWearingPartRequest, IUpdateWearingPartResponse, IWearingPartRevision } from '../../devices/models/wearing-part';
import { WearingPartService } from '../../devices/services/wearing-part.service';

export interface WearingPartState {
  loading: boolean;
  wearingParts: IGetWearingPartResponse[];
  groupedWearingParts: IWearingPartRevision[][];
}

@Injectable()
export class WearingPartStore extends ComponentStore<WearingPartState> {

  // Initial state
  constructor(
    private wearingPartsService: WearingPartService,
    private appnotificationService: AppNotificationService) {
      super({ 
        loading: false,
        wearingParts: [],
        groupedWearingParts: []
      });
  }

  // Selector for the list of wearing parts
  readonly loading$ = this.select((state) => state.loading);
  readonly wearingParts$: Observable<IGetWearingPartResponse[]> = this.select(state => state.wearingParts);
  readonly groupedWearingParts$: Observable<IWearingPartRevision[][]> = this.select(state => state.groupedWearingParts);

  // Effect to load wearing parts by device
  readonly getWearingPartsByDevice = this.effect<string>(deviceId$ =>
    deviceId$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(deviceId =>
        this.wearingPartsService.getWearingPartsByDevice(deviceId).pipe(
          tap(wearingParts => {
            const sortedWearingParts = wearingParts.sort((a, b) => {
              if (a.product.isActive !== b.product.isActive) {
                return a.product.isActive ? -1 : 1;
              }
              return a.product.internalNumber.localeCompare(b.product.internalNumber);
            });

            this.patchState(state => ({
              wearingParts: [...state.wearingParts, ...sortedWearingParts],
              loading: false
            }));
          })
        )
      ),
      take(1)
    )
  );


  // Effect to load wearing parts by devices
  readonly getWearingPartsByDevices = this.effect<string[]>(deviceIds$ =>
    deviceIds$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(deviceIds => {
        if (!deviceIds || deviceIds.length === 0) {
          // If no device IDs provided, return an empty observable
          return of([]);
        }
  
        // Use forkJoin to make parallel API calls for each deviceId
        const wearingPartsRequests = deviceIds.map(deviceId =>
          this.wearingPartsService.getWearingPartsByDevice(deviceId)
        );
  
        // Wait for all requests to complete
        return forkJoin(wearingPartsRequests).pipe(
          tap(wearingPartsResponses => {
            // Now, instead of using key-value, we group the parts into arrays per device
            const groupedWearingParts = wearingPartsResponses.map(response => response);

            // Replace the existing state with the new array of arrays
            this.patchState(state => ({
              groupedWearingParts: groupedWearingParts,
              loading: false
            }));
          })
        );
      })
    )
  );

  // Effect to load wearing parts by revision (NEW EFFECT)
  readonly getWearingPartsByRevision = this.effect<string>(revisionId$ =>
    revisionId$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(revisionId =>
        this.wearingPartsService.getWearingPartsByRevision(revisionId).pipe(
          tap(wearingParts => {
            const sortedWearingParts = wearingParts.data.sort((a, b) => 
              a.product.internalNumber.localeCompare(b.product.internalNumber));

            this.patchState(state => ({
              wearingParts: sortedWearingParts,
              loading: false
            }));
          }),
          catchError((error) => {
            this.appnotificationService.notification$.next({ message: translate('wearingPart.fetchFailed'), style: 'error' });
            return EMPTY;
          })
        )
      )
    )
  );
  
  


  // Method to add a wearing part to store
  readonly addWearingPart = this.effect<ICreateWearingPartRequest>(wearingPart$ =>
    wearingPart$.pipe(
      switchMap(wearingPart =>
        this.wearingPartsService.createWearingPart(wearingPart).pipe(
          tap((newWearingPart: ICreateWearingPartResponse) => {
            this.patchState(state => {
              const convertedWearingPart: IGetWearingPartResponse = {
                ...newWearingPart,
                // Add the missing fields from the original request or elsewhere
                deviceId: wearingPart.deviceId,
              };

              // create or update wearing parts
              var updatedWearingParts;
              if (convertedWearingPart.status == "Created") {
                updatedWearingParts = [...state.wearingParts, convertedWearingPart].sort((a, b) => {
                  if (a.product.isActive !== b.product.isActive) {
                    return a.product.isActive ? -1 : 1;
                  }
                  return a.product.internalNumber.localeCompare(b.product.internalNumber);
                });
              } else if (convertedWearingPart.status == "Updated") {
                updatedWearingParts = state.wearingParts.map(n =>
                  n.id === convertedWearingPart.id ? convertedWearingPart : n
                )
              }
  
              // Return state
              return { wearingParts: updatedWearingParts } as Partial<WearingPartState>;
            });
  
            if (newWearingPart) {
              if (newWearingPart.status == "Created") {
                this.appnotificationService.notification$.next({ message: translate('wearingPart.createSuccess'), style: 'success' });
              } else if (newWearingPart.status == "Updated") {
                this.appnotificationService.notification$.next({ message: translate('wearingPart.updateSuccess'), style: 'success' });
              }
            } else {
              this.appnotificationService.notification$.next({ message: translate('wearingPart.createFailed'), style: 'error' });
            }
          })
        )
      )
    )
  );
  
  
  // Effect to update a wearing part by its ID
  readonly updateWearingPart = this.effect<IUpdateWearingPartRequest>(wearingPart$ =>
    wearingPart$.pipe(
      switchMap(wearingPart =>
        this.wearingPartsService.updateWearingPart(wearingPart).pipe(
          tap((updatedWearingPart: IUpdateWearingPartResponse) => {
            this.patchState(state => {
              // Convert `IUpdateWearingPartResponse` to `IGetWearingPartResponse`
              const convertedWearingPart: IGetWearingPartResponse = {
                ...updatedWearingPart,
                // Add the missing fields
                deviceId: wearingPart.deviceId,
              };

              var updatedWearingParts;
              if (convertedWearingPart.status == "Merged") {
                // Filter out the merged part by its ID
                updatedWearingParts = state.wearingParts.filter(
                  n => n.id !== wearingPart.id
                ).map(n =>
                  n.id === convertedWearingPart.id ? convertedWearingPart : n
                );
              } else {
                updatedWearingParts = state.wearingParts.map(n =>
                  n.id === convertedWearingPart.id ? convertedWearingPart : n
                )
              }

              // Return state
              return { wearingParts: updatedWearingParts } as Partial<WearingPartState>;
            });
  
            // Notify success or failure
            if (updatedWearingPart) {
              this.appnotificationService.notification$.next({ message: translate('wearingPart.updateSuccess'), style: 'success' });
            } else {
              this.appnotificationService.notification$.next({ message: translate('wearingPart.updateFailed'), style: 'error' });
            }
          })
        )
      )
    )
  );
  
  

  // Method to remove a wearing part from store by its ID
  readonly removeWearingPartById = this.effect<string>(wearingPartId$ =>
    wearingPartId$.pipe(
      switchMap(wearingPartId =>
        this.wearingPartsService.deleteWearingPart(wearingPartId).pipe(
          tap(() => {
            this.patchState(state => ({
              wearingParts: state.wearingParts.filter(wearingPart => wearingPart.id !== wearingPartId)
            }));
            this.appnotificationService.notification$.next({ message:translate('wearingPart.deleteSuccess'), style: 'success' });
          }),
          catchError((error) => {
            return EMPTY; 
          })
        )
      )
    )
  );

  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );
}
